import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CrmShell, PageHeader, Button, Modal } from '../../components/CrmShell';
import { ActionMenu } from '../../components/ActionMenu';
import { EmptyState, Field, formatCompactMoney, formatDate, formatMoney, inputClass, PaginationControls, TABLE_PAGE_SIZE } from '../../components/CrmForms';
import { Company, Contact, Deal } from '../../types';
import {
  createCompany,
  deleteCompany,
  subscribeToCompanies,
  subscribeToContacts,
  subscribeToDeals,
  updateCompany,
} from '../../services/crm';
import { CompanyFinanceReport, CompanyFinanceSummary, getCompanyFinanceReport } from '../../services/companyFinance';
import { logGlobalError } from '../../utils/globalErrorLogger';

export function CompaniesPage() {
  const { companyId } = useParams();
  return companyId ? <CompanyDetailPage companyId={companyId} /> : <CompaniesListPage />;
}

function CompaniesListPage() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [editing, setEditing] = useState<Company | 'new' | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubs = [
      subscribeToCompanies(setCompanies, (err) => setError(err.message)),
      subscribeToContacts(setContacts, (err) => setError(err.message)),
      subscribeToDeals(setDeals, (err) => setError(err.message)),
    ];
    return () => unsubs.forEach((unsub) => unsub());
  }, []);

  const filtered = companies.filter((company) =>
    [company.name, company.website, company.industry].join(' ').toLowerCase().includes(query.toLowerCase()),
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / TABLE_PAGE_SIZE));
  const pagedCompanies = filtered.slice((page - 1) * TABLE_PAGE_SIZE, page * TABLE_PAGE_SIZE);
  const contactCounts = useMemo(() => {
    const counts = new Map<string, number>();
    contacts.forEach((contact) => {
      if (contact.companyId) counts.set(contact.companyId, (counts.get(contact.companyId) || 0) + 1);
    });
    return counts;
  }, [contacts]);
  const openDealCounts = useMemo(() => {
    const counts = new Map<string, number>();
    deals.forEach((deal) => {
      if (deal.companyId && !['Closed Won', 'Closed Lost'].includes(deal.stage)) {
        counts.set(deal.companyId, (counts.get(deal.companyId) || 0) + 1);
      }
    });
    return counts;
  }, [deals]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  async function saveCompany(values: CompanyFormValues) {
    const target = editing;
    setEditing(null);
    try {
      if (target && target !== 'new') await updateCompany(target.id, values);
      else await createCompany(values);
    } catch (err: any) {
      setError(err?.message || 'Failed to save company');
      logGlobalError(err, { context: 'Companies.saveCompany' });
    }
  }

  async function removeCompany(company: Company) {
    if (!window.confirm(`Delete ${company.name}?`)) return;
    await deleteCompany(company.id);
  }

  return (
    <CrmShell>
      <PageHeader
        title="Companies"
        description="Manage accounts and see linked contacts/deals."
        action={<Button onClick={() => setEditing('new')}><Plus className="mr-1 inline h-4 w-4" />New company</Button>}
      />
      {error && <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <div className="mb-4">
        <input
          className={inputClass}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          placeholder="Search companies..."
        />
      </div>
      {filtered.length ? (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-[960px] w-full text-sm">
              <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2">Company</th>
                  <th className="px-3 py-2">Industry</th>
                  <th className="px-3 py-2">Employee size</th>
                  <th className="px-3 py-2">Contacts</th>
                  <th className="px-3 py-2">Open deals</th>
                  <th className="px-3 py-2">Last activity</th>
                  <th className="px-3 py-2"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedCompanies.map((company) => (
                  <tr key={company.id}>
                    <td className="px-3 py-2">
                      <Link to={`/companies/${company.id}`} className="font-medium text-primary hover:underline">{company.name}</Link>
                      <div className="text-xs text-gray-500">{company.website || '—'}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{company.industry || '—'}</td>
                    <td className="px-3 py-2 text-gray-500">{company.size || '—'}</td>
                    <td className="px-3 py-2 text-gray-500">{contactCounts.get(company.id) || 0}</td>
                    <td className="px-3 py-2 text-gray-500">{openDealCounts.get(company.id) || 0}</td>
                    <td className="px-3 py-2 text-gray-500">{formatDate(company.lastActivityAt)}</td>
                    <td className="px-3 py-2 text-right">
                      <ActionMenu
                        label={`Actions for ${company.name}`}
                        items={[
                          { id: 'edit', label: 'Edit', icon: <Pencil className="h-4 w-4" />, onSelect: () => setEditing(company) },
                          { id: 'delete', label: 'Delete', icon: <Trash2 className="h-4 w-4" />, onSelect: () => removeCompany(company), tone: 'danger' },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls page={page} totalItems={filtered.length} onPageChange={setPage} />
        </div>
      ) : <EmptyState>No companies yet.</EmptyState>}
      {editing && (
        <Modal title={editing === 'new' ? 'New company' : 'Edit company'} onClose={() => setEditing(null)}>
          <CompanyForm company={editing === 'new' ? null : editing} onSubmit={saveCompany} />
        </Modal>
      )}
    </CrmShell>
  );
}

function CompanyDetailPage({ companyId }: { companyId: string }) {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [editing, setEditing] = useState(false);
  const [financeReport, setFinanceReport] = useState<CompanyFinanceReport | null>(null);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeError, setFinanceError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubs = [
      subscribeToCompanies(setCompanies, (err) => setError(err.message)),
      subscribeToContacts(setContacts, (err) => setError(err.message)),
      subscribeToDeals(setDeals, (err) => setError(err.message)),
    ];
    return () => unsubs.forEach((unsub) => unsub());
  }, []);

  const company = companies.find((item) => item.id === companyId) ?? null;
  const companyContacts = contacts.filter((contact) => contact.companyId === companyId);
  const companyDeals = deals.filter((deal) => deal.companyId === companyId);
  const openDeals = companyDeals.filter((deal) => !['Closed Won', 'Closed Lost'].includes(deal.stage));

  useEffect(() => {
    const alignedCustomerId = company?.alignedCustomerId?.trim();
    if (!alignedCustomerId) {
      setFinanceReport(null);
      setFinanceError(null);
      setFinanceLoading(false);
      return;
    }

    let cancelled = false;
    setFinanceLoading(true);
    setFinanceError(null);
    getCompanyFinanceReport(alignedCustomerId)
      .then((report) => {
        if (!cancelled) setFinanceReport(report);
      })
      .catch((err) => {
        if (cancelled) return;
        setFinanceError(err?.message || 'Failed to load finance data');
        logGlobalError(err, { context: 'CompanyDetail.loadFinanceReport' });
      })
      .finally(() => {
        if (!cancelled) setFinanceLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [company?.alignedCustomerId]);

  async function saveCompany(values: CompanyFormValues) {
    setEditing(false);
    try {
      await updateCompany(companyId, values);
    } catch (err: any) {
      setError(err?.message || 'Failed to save company');
      logGlobalError(err, { context: 'CompanyDetail.saveCompany' });
    }
  }

  async function removeCompany() {
    if (!company || !window.confirm(`Delete ${company.name}?`)) return;
    await deleteCompany(company.id);
    navigate('/companies');
  }

  return (
    <CrmShell>
      <PageHeader
        title={company?.name ?? 'Company'}
        description={company?.website || company?.industry}
        action={company && <div className="flex flex-wrap gap-2"><Button variant="secondary" onClick={() => setEditing(true)}>Edit</Button><Button variant="danger" onClick={removeCompany}>Delete</Button></div>}
      />
      {error && <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {!company ? <EmptyState>Company not found.</EmptyState> : (
        <div className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr]">
            <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-900">Details</h2>
              <dl className="mt-4 space-y-3 text-sm">
                <Detail label="Aligned Customer Id" value={company.alignedCustomerId} />
                <Detail label="Website" value={company.website} />
                <Detail label="Industry" value={company.industry} />
                <Detail label="Employee size" value={company.size} />
                <Detail label="Last activity" value={formatDate(company.lastActivityAt)} />
                <Detail label="Notes" value={company.notes} />
              </dl>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <Summary title="Open pipeline" value={formatMoney(openDeals.reduce((sum, deal) => sum + deal.value, 0))} />
                <Summary title="Contacts" value={companyContacts.length} />
              </div>
            </section>
            <section className="space-y-6">
              <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                <h2 className="text-sm font-semibold text-gray-900">Contacts</h2>
                <div className="mt-4 space-y-2">
                  {companyContacts.length ? companyContacts.map((contact) => (
                    <Link key={contact.id} to={`/contacts/${contact.id}`} className="block rounded border border-gray-200 p-3 text-sm hover:bg-gray-50">
                      <span className="font-medium text-primary">{contact.firstName} {contact.lastName}</span>
                      <span className="ml-2 text-gray-500">{contact.email}</span>
                    </Link>
                  )) : <p className="text-sm text-gray-500">No contacts linked.</p>}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                <h2 className="text-sm font-semibold text-gray-900">Deals</h2>
                <div className="mt-4 space-y-2">
                  {companyDeals.length ? companyDeals.map((deal) => (
                    <Link key={deal.id} to={`/deals/${deal.id}`} className="flex items-center justify-between gap-3 rounded border border-gray-200 p-3 text-sm hover:bg-gray-50">
                      <span><span className="font-medium text-primary">{deal.name}</span><span className="ml-2 text-gray-500">{deal.stage}</span></span>
                      <span className="font-semibold text-gray-900">{formatMoney(deal.value)}</span>
                    </Link>
                  )) : <p className="text-sm text-gray-500">No deals linked.</p>}
                </div>
              </div>
            </section>
          </div>
          <CompanyFinanceSection
            alignedCustomerId={company.alignedCustomerId}
            report={financeReport}
            loading={financeLoading}
            error={financeError}
          />
        </div>
      )}
      {editing && company && (
        <Modal title="Edit company" onClose={() => setEditing(false)}>
          <CompanyForm company={company} onSubmit={saveCompany} />
        </Modal>
      )}
    </CrmShell>
  );
}

function Summary({ title, value }: { title: string; value: string | number }) {
  return (
    <div className="rounded border border-gray-200 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{title}</div>
      <div className="mt-1 text-lg font-semibold text-gray-900">{value}</div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap text-gray-800">{value || '—'}</dd>
    </div>
  );
}

function CompanyFinanceSection({
  alignedCustomerId,
  report,
  loading,
  error,
}: {
  alignedCustomerId: string;
  report: CompanyFinanceReport | null;
  loading: boolean;
  error: string | null;
}) {
  const summary = report?.summary ?? null;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Current spend</h2>
          <p className="mt-1 text-xs text-gray-500">
            {alignedCustomerId ? `Aligned customer ${alignedCustomerId}` : 'Add an Aligned Customer Id to sync finance data.'}
          </p>
        </div>
        {report?.generatedAt && (
          <span className="text-xs text-gray-500">Updated {formatDate(new Date(report.generatedAt))}</span>
        )}
      </div>

      {!alignedCustomerId ? (
        <div className="mt-4"><EmptyState>No Aligned Customer Id set.</EmptyState></div>
      ) : loading ? (
        <p className="mt-4 text-sm text-gray-500">Loading finance data...</p>
      ) : error ? (
        <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      ) : !summary ? (
        <div className="mt-4"><EmptyState>No finance data found for this customer.</EmptyState></div>
      ) : (
        <CompanyFinanceSummaryView summary={summary} />
      )}
    </section>
  );
}

function CompanyFinanceSummaryView({ summary }: { summary: CompanyFinanceSummary }) {
  const rows = [
    { name: 'Paid', value: summary.paid },
    { name: 'Open', value: summary.open },
    { name: 'Overdue', value: summary.overdue },
    { name: 'Pending', value: summary.pending },
  ];
  const total = rows.reduce((sum, row) => sum + row.value, 0);

  return (
    <div className="mt-4 space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Summary title="Paid" value={formatMicroUsd(summary.paid)} />
        <Summary title="Open" value={formatMicroUsd(summary.open)} />
        <Summary title="Overdue" value={formatMicroUsd(summary.overdue)} />
        <Summary title="Pending" value={formatMicroUsd(summary.pending)} />
        <Summary title="Total" value={formatMicroUsd(total)} />
      </div>

      <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="h-72">
          {total > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows.filter((row) => row.value > 0)} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(value) => formatCompactMoney(microUsdToDollars(Number(value)))} />
                <YAxis dataKey="name" type="category" width={72} />
                <Tooltip formatter={(value) => formatMicroUsd(Number(value))} />
                <Bar dataKey="value" fill="#25286a" radius={[0, 4, 4, 0]} barSize={28} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState>No current spend.</EmptyState>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200">
          <div className="max-h-72 overflow-auto">
            <table className="min-w-[680px] w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2">PO</th>
                  <th className="px-3 py-2 text-right">Paid</th>
                  <th className="px-3 py-2 text-right">Open</th>
                  <th className="px-3 py-2 text-right">Overdue</th>
                  <th className="px-3 py-2 text-right">Pending</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {summary.poSummaries.length ? summary.poSummaries.map((po) => {
                  const poTotal = po.paid + po.open + po.overdue + po.pending;
                  return (
                    <tr key={`${po.purchaseOrderId || 'legacy'}-${po.poNumber}`}>
                      <td className="px-3 py-2 font-medium text-gray-900">{po.poNumber}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{formatMicroUsd(po.paid)}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{formatMicroUsd(po.open)}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{formatMicroUsd(po.overdue)}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{formatMicroUsd(po.pending)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900">{formatMicroUsd(poTotal)}</td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td className="px-3 py-6 text-center text-gray-500" colSpan={6}>No PO breakdown.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function microUsdToDollars(value: number): number {
  return value / 1_000_000;
}

function formatMicroUsd(value: number): string {
  return formatMoney(microUsdToDollars(value));
}

interface CompanyFormValues {
  name: string;
  alignedCustomerId: string;
  website: string;
  industry: string;
  size: string;
  notes: string;
}

function CompanyForm({ company, onSubmit }: { company: Company | null; onSubmit: (values: CompanyFormValues) => Promise<void> }) {
  const [values, setValues] = useState<CompanyFormValues>({
    name: company?.name ?? '',
    alignedCustomerId: company?.alignedCustomerId ?? '',
    website: company?.website ?? '',
    industry: company?.industry ?? '',
    size: company?.size ?? '',
    notes: company?.notes ?? '',
  });
  const [saving, setSaving] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit(values);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
      <Field label="Name"><input className={inputClass} value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} required /></Field>
      <Field label="Aligned Customer Id"><input className={inputClass} value={values.alignedCustomerId} onChange={(e) => setValues({ ...values, alignedCustomerId: e.target.value })} /></Field>
      <Field label="Website"><input className={inputClass} value={values.website} onChange={(e) => setValues({ ...values, website: e.target.value })} /></Field>
      <Field label="Industry"><input className={inputClass} value={values.industry} onChange={(e) => setValues({ ...values, industry: e.target.value })} /></Field>
      <Field label="Employee size"><input className={inputClass} value={values.size} onChange={(e) => setValues({ ...values, size: e.target.value })} /></Field>
      <div className="sm:col-span-2"><Field label="Notes"><textarea className={inputClass} rows={4} value={values.notes} onChange={(e) => setValues({ ...values, notes: e.target.value })} /></Field></div>
      <div className="sm:col-span-2"><Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save company'}</Button></div>
    </form>
  );
}
